import { useState, useMemo, useEffect } from 'react';
import {
  MEAL_PLAN_DAY_ORDER,
  COLD_START_PLANNER_HERO,
  isRestOfWeekDinnerDay,
  onboardingPlanLead,
  countPlannedDinners,
  onboardingPlanningIsGated,
  onboardingShouldAskToSave,
  defaultPlannerCalendarContext,
  plannerGroceryMeals,
  plannerTotalMissingCount,
  resolveKitchenStartedAt,
  shouldPromptPastNightResolve,
} from '../../utils/mealPlanHelpers.js';
import MobileMealCard    from './MobileMealCard.jsx';
import EmptyDayRow       from './EmptyDayRow.jsx';
import MobileMoveMealSheet from './MobileMoveMealSheet.jsx';
import CompactGroceryRail from './CompactGroceryRail.jsx';
import RecipePackShelf   from './RecipePackShelf.jsx';
import PlanCookProgress, { PlanWeekStrip } from './PlanCookProgress.jsx';
import PlanWeekListHeader from './PlanWeekListHeader.jsx';
import PlanWeekSectionHeader from './PlanWeekSectionHeader.jsx';
import { countWeekCoverage } from '../../utils/nightOff.js';
import { plannerAssistantEnabled } from '../../utils/dinnersSection.js';
import {
  allocatePreview,
  openPlanDayKeys,
  receiptLine,
  seedDraft,
  weekHasPastNight,
} from '../../utils/planWithCookPreview.js';
import SwapRecipeSheet   from './SwapRecipeSheet.jsx';
import StateBanner from '../StateBanner/StateBanner.jsx';
import SharedEmptyHero from '../StateBanner/EmptyHero.jsx';
import PlanWithCookSheet from './PlanWithCookSheet.jsx'
import WeekNavigationBar from './WeekNavigationBar.jsx'
import StatusPill from '../../design-system/patterns/StatusPill.jsx'
import MoveMealConflictModal from './MoveMealConflictModal.jsx'
import { useAuth } from '../../contexts/AuthContext.jsx'
import './planner.css';

const PLANNER_ASSISTANT_ENABLED = plannerAssistantEnabled()

/*
 * MobilePlanner — mobile layout for the Week Planner (live meal plan API only).
 */
export default function MobilePlanner({
  plannerState,
  dietary,
  weekLabel,
  weekNav = null,
  onSwitchToRecipes = null,
  onImportRecipe = null,
  libraryCount = null,
  starterPacks = [],
  addedPackIds = new Set(),
  onApplyStarterPack = null,
  isMobile = true,
  livePlannerApi,
  liveBanner = null,
  onDismissLiveBanner = null,
  onViewRecipe = null,
  onSavePrompt = null,
  saveAskDismissed = false,
  highlightRecipeId = null,
  guestMode = false,
  onboardingMode = false,
  ensureAssignedIngredientLines = null,
  retryAssignedIngredientLines = null,
  assignedIngredientsLoading = false,
  assignedIngredientsError = false,
}) {
  const [moveMeal, setMoveMeal] = useState(null);
  const [moveMealAnchorRect, setMoveMealAnchorRect] = useState(null);
  const [swapMeal, setSwapMeal] = useState(null);
  const [addDayKey, setAddDayKey] = useState(null);
  const [planSheetOpen, setPlanSheetOpen] = useState(false);
  const [planSeed, setPlanSeed] = useState(null);
  const [planDayKeys, setPlanDayKeys] = useState([]);
  const [groceryExpanded, setGroceryExpanded] = useState(false);
  const [groceryFilter, setGroceryFilter] = useState('missing');

  const onGroceryFilter = (next) => {
    setGroceryFilter(next)
    if (next === 'all') void ensureAssignedIngredientLines?.()
  }

  useEffect(() => {
    if (groceryFilter !== 'all' || assignedIngredientsError) return
    void ensureAssignedIngredientLines?.()
  }, [
    groceryFilter,
    assignedIngredientsError,
    ensureAssignedIngredientLines,
    livePlannerApi?.weekStartDate,
    livePlannerApi?.assignedMeals,
  ])
  const { user } = useAuth();

  // Mobile: date range only — drop "Week N ·" so the header has room to breathe.
  const mobileWeekLabel = useMemo(
    () => String(weekLabel || '').replace(/^Week \d+\s*·\s*/, ''),
    [weekLabel],
  );

  const rawCold = plannerState === 'cold-start';
  const isCold = rawCold && !onboardingMode;
  const displayBanner = onboardingMode ? null : (liveBanner || null);
  const highlightId = highlightRecipeId != null ? String(highlightRecipeId) : null;
  const mealIsHighlighted = (m) => Boolean(highlightId && m?.recipeId && String(m.recipeId) === highlightId);

  const packsMode = onboardingMode
    ? 'none'
    : rawCold ? 'hero' : plannerState === 'thin-library' ? 'shelf' : 'none';

  const assigned = useMemo(() => {
    return [...livePlannerApi.assignedMeals].sort(
      (a, b) => MEAL_PLAN_DAY_ORDER.indexOf(a.assignedTo) - MEAL_PLAN_DAY_ORDER.indexOf(b.assignedTo),
    );
  }, [livePlannerApi.assignedMeals]);

  const byDay = useMemo(
    () => Object.fromEntries(assigned.map(m => [m.assignedTo, m])),
    [assigned],
  );
  const stripFallback = defaultPlannerCalendarContext();
  const cal = livePlannerApi.calendarContext;
  const dates = cal?.dayDates ?? stripFallback.dayDates;
  const todayKey = cal != null ? cal.todayKey : stripFallback.todayKey;
  const stripDays = useMemo(
    () => livePlannerApi.stripDays || [],
    [livePlannerApi.stripDays],
  );
  const openDayKeys = useMemo(() => {
    const keys = openPlanDayKeys(byDay, stripDays);
    if (!onboardingMode) return keys;
    return keys.filter((key) => isRestOfWeekDinnerDay(key, todayKey));
  }, [byDay, onboardingMode, stripDays, todayKey]);
  const kitchenStartedAt = resolveKitchenStartedAt({
    accountCreatedAt: user?.created_at || null,
    planGeneratedAt: livePlannerApi.generatedAt || null,
    guestMode,
    guestTrial: Boolean(livePlannerApi.planMetadata?.guest_trial),
  });
  const promptPastNight = (dayKey, slotDate = null) =>
    shouldPromptPastNightResolve({
      slotDateIso: slotDate,
      weekStartIso: livePlannerApi.weekStartDate,
      dayKey,
      kitchenStartedAt,
    });

  function openMoveSheet(meal, anchorRect = null) {
    if (!meal || meal.isPast) return;
    setGroceryExpanded(false);
    setSwapMeal(null);
    setAddDayKey(null);
    setPlanSheetOpen(false);
    setMoveMealAnchorRect(anchorRect);
    setMoveMeal(meal);
  }

  function closeMoveSheet() {
    setMoveMeal(null);
    setMoveMealAnchorRect(null);
  }

  function dinnersOnWeek() {
    return countPlannedDinners(livePlannerApi.assignedMeals)
  }

  function planningIsGated() {
    return onboardingMode && onboardingPlanningIsGated(dinnersOnWeek(), saveAskDismissed)
  }

  function holdForSave() {
    if (!planningIsGated()) return false
    onSavePrompt?.()
    return true
  }

  function promptSaveIfSecondDinner(dinnersAfter) {
    if (!onboardingMode || !onboardingShouldAskToSave(dinnersAfter, saveAskDismissed)) return
    onSavePrompt?.()
  }

  function openAddDinner(dayKey) {
    if (holdForSave()) return
    const stripDay = stripDays.find((d) => d.dayKey === dayKey);
    if (stripDay?.isPast) return;
    setGroceryExpanded(false);
    setMoveMeal(null);
    setMoveMealAnchorRect(null);
    setSwapMeal(null);
    setPlanSheetOpen(false);
    setAddDayKey(dayKey);
  }

  function closeRecipePicker() {
    setSwapMeal(null);
    setAddDayKey(null);
  }

  function closePlanSheet() {
    setPlanSheetOpen(false);
    requestAnimationFrame(() => {
      const adjust = document.getElementById('plan-with-cook-adjust');
      const pill = document.getElementById('plan-with-cook-pill');
      (adjust || pill)?.focus();
    });
  }

  function openPlanSheet(adjust = false) {
    if (holdForSave()) return
    setSwapMeal(null);
    setAddDayKey(null);
    setMoveMeal(null);
    setMoveMealAnchorRect(null);
    setGroceryExpanded(false);
    const flow = livePlannerApi.planCookFlow;
    if (adjust && flow?.draft) {
      setPlanDayKeys(flow.draft.plannedDayKeys || openDayKeys);
      setPlanSeed({ ...flow.draft, replaceExisting: true });
    } else {
      setPlanDayKeys(openDayKeys);
      setPlanSeed(seedDraft(livePlannerApi.mealPlanStructure, {
        weekFull: openDayKeys.length === 0,
        openDayKeys,
        weekHasPast: weekHasPastNight(stripDays),
      }));
    }
    setPlanSheetOpen(true);
  }

  async function submitPlan(draft) {
    const dinnersBefore = dinnersOnWeek()
    const [cookDays] = allocatePreview(draft, planDayKeys)
    closePlanSheet();
    const planned = await livePlannerApi.runPlanWithCook?.({
      ...draft,
      plannedDayKeys: planDayKeys,
      receipt: receiptLine(draft, planDayKeys),
    });
    if (planned) promptSaveIfSecondDinner(dinnersBefore + cookDays.length);
  }

  async function selectMoveDay(stripDay) {
    if (!moveMeal || !stripDay) return;
    const moving = moveMeal;
    setMoveMeal(null);
    setMoveMealAnchorRect(null);
    await livePlannerApi.assignToStripDay(moving, stripDay);
  }

  function unassign(id) {
    const m = livePlannerApi.assignedMeals.find(x => x.id === id);
    if (m) void livePlannerApi.unassignMeal(m);
  }

  function removePick(meal) {
    if (!meal?.recipeId) return;
    void livePlannerApi.removePlannerCandidate(meal.recipeId, meal.name);
  }

  const heroTitle = weekNav?.heroTitle
    ? weekNav.heroTitle(isCold)
    : (isCold ? 'Almost ready to plan' : 'Plan Week');
  const assistantEnabledForWeek =
    PLANNER_ASSISTANT_ENABLED && (!weekNav || weekNav.isCurrentWeek || weekNav.isFutureWeek);

  useEffect(() => {
    if (!moveMeal) return undefined;
    const previousOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('.main-content');
    const previousMainOverflow = mainEl?.style.overflow;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMoveMeal(null);
        setMoveMealAnchorRect(null);
      }
    };
    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      if (mainEl) mainEl.style.overflow = previousMainOverflow ?? '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [moveMeal]);

  function addMissingToShop(opts = {}) {
    livePlannerApi.buildGroceryList({ openShop: true, ...opts }).then((data) => {
      if (data) setGroceryExpanded(false);
    }).catch(() => {
      // Toast is shown inside the hook; swallow so React doesn't see an unhandled rejection.
    });
  }

  const showPlanWithCook = onboardingMode || !guestMode
  const showLegacyStrip = !onboardingMode && (guestMode || isCold)

  return (
    <div className={`mobile-planner${onboardingMode ? ' mobile-planner--onboarding' : ''}`}>
      <div className="mobile-planner-header">
        {!onboardingMode && (
        <div className="planner-hero-heading">
          <div className="planner-hero-title-block">
            <div className="page-intro-eyebrow-slot">
              <p className="eyebrow planner-eyebrow-xs">Plan</p>
            </div>
            <div className="h-display planner-title-lg">
              {heroTitle}
            </div>
          </div>
          {weekNav ? (
            <WeekNavigationBar
              weekLabel={mobileWeekLabel}
              canGoPrevious={weekNav.canGoPrevious}
              canGoNext={weekNav.canGoNext}
              onPreviousWeek={weekNav.goToPreviousWeek}
              onNextWeek={weekNav.goToNextWeek}
              loading={weekNav.loading}
            />
          ) : null}
        </div>
        )}
        {!isCold && livePlannerApi.notice && (
          <div className="planner-mobile-banner-row">
            <StatusPill
              variant={livePlannerApi.notice.kind || 'info'}
              onDismiss={livePlannerApi.clearNotice}
            >
              <span className="planner-notice-text">{livePlannerApi.notice.text}</span>
              {livePlannerApi.notice.actionLabel ? (
                <button
                  type="button"
                  className="planner-notice-action"
                  onClick={() => livePlannerApi.runNoticeAction?.()}
                >
                  {livePlannerApi.notice.actionLabel}
                </button>
              ) : null}
            </StatusPill>
          </div>
        )}
        {!isCold && !onboardingMode && livePlannerApi.weekStartDate && (
          <PlanCookProgress
            assigned={assigned}
            weekStartIso={livePlannerApi.weekStartDate}
            accountCreatedAt={user?.created_at || null}
            density="mobile"
            showWeekStrip={!guestMode}
            assignedByDay={byDay}
            todayKey={todayKey}
            pastDayKeys={cal?.pastDayKeys}
          />
        )}
        {showLegacyStrip && (
          <PlanWeekListHeader
            assignedByDay={byDay}
            dayDates={dates}
            todayKey={todayKey}
            density="mobile"
            dimmed={isCold}
          />
        )}

        {isCold && (
          <MobilePlannerEmptyHero
            libraryCount={libraryCount}
            onSwitchToRecipes={onSwitchToRecipes}
            onImportRecipe={onImportRecipe}
          />
        )}

        {displayBanner && (
          <MobilePlannerStateBanner
            banner={displayBanner}
            onDismiss={displayBanner.dismissible ? onDismissLiveBanner : null}
          />
        )}

        {onboardingMode && (
          <div className="planner-onboarding-head">
            <PlanWeekSectionHeader
              coverage={countWeekCoverage(byDay)}
              canPlan={assistantEnabledForWeek}
              busy={livePlannerApi.planCookFlow?.status === 'loading'}
              status={livePlannerApi.planCookFlow?.status || 'idle'}
              error={livePlannerApi.planCookFlow?.error || ''}
              receipt={livePlannerApi.planCookFlow?.draft?.receipt || ''}
              density="mobile"
              onPlan={() => openPlanSheet(false)}
              onAdjust={() => openPlanSheet(true)}
              headingLevel={1}
              lead={onboardingPlanLead()}
            />
          </div>
        )}
        {!isCold && (
          <div className={`planner-week-list${showPlanWithCook && !onboardingMode ? ' planner-week-list--headed' : ''}${onboardingMode ? ' planner-week-list--onboarding' : ''}`}>
            {showPlanWithCook && !onboardingMode && (
              <PlanWeekSectionHeader
                coverage={countWeekCoverage(byDay)}
                canPlan={assistantEnabledForWeek}
                busy={livePlannerApi.planCookFlow?.status === 'loading'}
                status={livePlannerApi.planCookFlow?.status || 'idle'}
                error={livePlannerApi.planCookFlow?.error || ''}
                receipt={livePlannerApi.planCookFlow?.draft?.receipt || ''}
                density="mobile"
                onPlan={() => openPlanSheet(false)}
                onAdjust={() => openPlanSheet(true)}
              />
            )}
            {onboardingMode && (
              <div className="pwl-strip">
                <PlanWeekStrip
                  assignedByDay={byDay}
                  todayKey={todayKey}
                  pastDayKeys={cal?.pastDayKeys}
                  compact
                />
              </div>
            )}
            <div
              className={`planner-week-rows${livePlannerApi.planCookFlow?.status === 'loading' ? ' is-planning' : ''}`}
              aria-busy={livePlannerApi.planCookFlow?.status === 'loading' || undefined}
            >
            {MEAL_PLAN_DAY_ORDER.filter((dayKey) => (
              !onboardingMode || isRestOfWeekDinnerDay(dayKey, todayKey)
            )).map((dayKey) => {
          const m = byDay[dayKey];
          if (!m) {
            const stripDay = stripDays.find((d) => d.dayKey === dayKey);
            const isPastEmpty = Boolean(stripDay?.isPast);
            return (
              <EmptyDayRow
                key={dayKey}
                dayKey={dayKey}
                dateNum={dates[dayKey]}
                isToday={todayKey != null && dayKey === todayKey}
                density="mobile"
                disabled={isPastEmpty}
                status={stripDay?.status || 'planned'}
                onAdd={() => openAddDinner(dayKey)}
                onMarkSkipped={
                  isPastEmpty &&
                  stripDay?.planItemId &&
                  stripDay?.status !== 'skipped' &&
                  promptPastNight(dayKey, stripDay?.slotDate)
                    ? () => livePlannerApi.markMealSkipped({
                      planItemId: stripDay.planItemId,
                      recipeId: null,
                      name: 'No dinner',
                    })
                    : undefined
                }
                onNightOff={
                  isPastEmpty &&
                  stripDay?.planItemId &&
                  stripDay?.status !== 'skipped' &&
                  promptPastNight(dayKey, stripDay?.slotDate)
                    ? (typeId) => livePlannerApi.setNightOff?.(
                      { planItemId: stripDay.planItemId },
                      typeId,
                    )
                    : undefined
                }
              />
            );
          }
          return (
            <MobileMealCard
              key={m.id}
              meal={m}
              state="assigned"
              isToday={todayKey != null && m.assignedTo === todayKey}
              isHighlighted={mealIsHighlighted(m)}
              onOpenMoveSheet={openMoveSheet}
              onUnassign={() => unassign(m.id)}
              onSwap={(meal) => {
                setAddDayKey(null);
                setMoveMeal(null);
                setMoveMealAnchorRect(null);
                setPlanSheetOpen(false);
                setSwapMeal(meal);
              }}
              dayDates={dates}
              onViewRecipe={!onboardingMode && onViewRecipe && m.recipeId ? () => onViewRecipe(m.recipeId) : undefined}
              onMarkCooked={
                m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                  ? () => livePlannerApi.markMealCooked(m)
                  : undefined
              }
              onMarkSkipped={
                m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                  ? () => livePlannerApi.markMealSkipped(m)
                  : undefined
              }
              onNightOff={
                m.isPast && m.recipeId && promptPastNight(m.assignedTo, m.slotDate)
                  ? (typeId) => livePlannerApi.setNightOff?.(m, typeId)
                  : undefined
              }
            />
          );
            })}
            </div>
          </div>
        )}

        {packsMode === 'hero' && (
          <RecipePackShelf
            mode="hero"
            packs={starterPacks}
            addedPackIds={addedPackIds}
            onApplyPack={onApplyStarterPack}
            onBrowseAllPacks={onImportRecipe ?? undefined}
            isMobile={isMobile}
          />
        )}

        {packsMode === 'shelf' && (
          <RecipePackShelf
            mode="shelf"
            packs={starterPacks}
            addedPackIds={addedPackIds}
            onApplyPack={onApplyStarterPack}
            onBrowseAllPacks={onImportRecipe ?? undefined}
            isMobile={isMobile}
          />
        )}
      </div>

      {!isCold && !onboardingMode && (
        <MobileGroceryCTA
          assigned={assigned}
          plannerState={plannerState}
          expanded={groceryExpanded}
          onToggleExpanded={() => {
            setMoveMeal(null);
            setSwapMeal(null);
            setAddDayKey(null);
            setPlanSheetOpen(false);
            setGroceryExpanded((prev) => !prev);
          }}
          groceryFilter={groceryFilter}
          onGroceryFilter={onGroceryFilter}
          onAddMissingToShop={addMissingToShop}
          buildBusy={livePlannerApi.busy}
          allIngredientsLoading={assignedIngredientsLoading}
          allIngredientsError={assignedIngredientsError}
          onRetryAllIngredients={retryAssignedIngredientLines}
        />
      )}

      <MobileMoveMealSheet
        open={Boolean(moveMeal)}
        meal={moveMeal}
        stripDays={stripDays}
        mealWeekStart={livePlannerApi.weekStartDate}
        busy={livePlannerApi.busy}
        anchorRect={moveMealAnchorRect}
        onClose={closeMoveSheet}
        onSelectDay={selectMoveDay}
      />

      <SwapRecipeSheet
        open={Boolean(swapMeal) || Boolean(addDayKey)}
        mode={swapMeal ? 'swap' : 'add'}
        meal={swapMeal}
        dayKey={addDayKey}
        candidates={livePlannerApi.suggestionMeals}
        plannerState={plannerState}
        dietary={dietary}
        libraryCount={libraryCount}
        onImportRecipe={onImportRecipe}
        highlightRecipeId={highlightRecipeId}
        onRemovePick={removePick}
        removePickBusy={livePlannerApi.busy}
        density="mobile"
        onSaveNightOff={onboardingMode ? undefined : async (dayKey, typeId) => {
          const ok = await livePlannerApi.setNightOff?.(dayKey, typeId)
          if (ok) closeRecipePicker()
        }}
        onClearNight={
          onboardingMode || swapMeal?.status !== 'night_off'
            ? undefined
            : () => {
                unassign(swapMeal.id)
                closeRecipePicker()
              }
        }
        onClose={closeRecipePicker}
        onPick={async (recipe) => {
          if (!recipe?.recipeId) return
          const adding = !swapMeal && Boolean(addDayKey)
          const dinnersBefore = dinnersOnWeek()
          let assigned = false
          if (swapMeal) {
            await livePlannerApi.swapRecipeInPlace?.(swapMeal, recipe.recipeId)
          } else if (addDayKey) {
            assigned = Boolean(await livePlannerApi.assignToDay?.(recipe, addDayKey))
          }
          closeRecipePicker()
          if (adding && assigned) promptSaveIfSecondDinner(dinnersBefore + 1)
        }}
      />

      <PlanWithCookSheet
        open={planSheetOpen}
        initialDraft={planSeed}
        openDayKeys={planDayKeys}
        previewRecipes={livePlannerApi.suggestionMeals || []}
        onboardingMode={onboardingMode}
        density="mobile"
        onClose={closePlanSheet}
        onSubmit={submitPlan}
      />

      <MoveMealConflictModal
        open={Boolean(livePlannerApi.pendingMoveConflict)}
        movingMeal={livePlannerApi.pendingMoveConflict?.movingMeal}
        stripDay={livePlannerApi.pendingMoveConflict?.stripDay}
        fromWeekStart={livePlannerApi.pendingMoveConflict?.fromWeekStart}
        busy={livePlannerApi.busy}
        onCancel={() => livePlannerApi.cancelMoveConflict?.()}
        onReplace={() => void livePlannerApi.resolveMoveConflict?.('replace')}
        onSwap={() => void livePlannerApi.resolveMoveConflict?.('swap')}
      />
    </div>
  );
}

function renderMobileBannerBody(banner) {
  if (!banner?.bodyStrong || typeof banner.body !== 'string') return banner?.body;

  const index = banner.body.indexOf(banner.bodyStrong);
  if (index === -1) return banner.body;

  return (
    <>
      {banner.body.slice(0, index)}
      <b>{banner.bodyStrong}</b>
      {banner.body.slice(index + banner.bodyStrong.length)}
    </>
  );
}

function MobilePlannerStateBanner({ banner, onDismiss }) {
  return (
    <StateBanner
      variant={banner.tone}
      eyebrow={banner.eyebrow}
      headline={banner.title}
      body={renderMobileBannerBody(banner)}
      dismissible={banner.tone === 'ai' && !!onDismiss}
      onDismiss={onDismiss}
      className="mobile-planner-state-banner"
    />
  );
}

function MobilePlannerEmptyHero({
  libraryCount = null,
  onSwitchToRecipes = null,
  onImportRecipe = null,
}) {
  const liveLibrary = typeof libraryCount === 'number' ? libraryCount : null;
  const needMore = liveLibrary != null ? Math.max(8 - liveLibrary, 3) : null;
  const liveCopy = liveLibrary == null
    ? null
    : (liveLibrary === 0
        ? {
            eyebrow: 'Almost ready to plan',
            title: 'Add a few recipes to get started',
            body: `Import about ${needMore} recipes you already cook, then add them to open nights.`,
          }
        : {
            eyebrow: 'Almost ready to plan',
            title: `${liveLibrary === 1 ? "You've got 1 recipe" : `You've got ${liveLibrary} recipes`} — add a few more`,
            body: `Add about ${needMore} more so the planner can rotate dinners.`,
          });
  const display = liveCopy || COLD_START_PLANNER_HERO;

  return (
    <SharedEmptyHero
      className="mobile-planner-empty-state-hero"
      eyebrow={display.eyebrow}
      headline={display.title}
      body={display.body}
      actions={[
        { label: 'Import recipe', kind: 'primary', onClick: onImportRecipe },
        { label: 'Browse', kind: 'secondary', onClick: onSwitchToRecipes },
      ]}
    />
  );
}

function MobileGroceryCTA({
  assigned,
  plannerState,
  expanded = false,
  onToggleExpanded,
  groceryFilter = 'missing',
  onGroceryFilter,
  onAddMissingToShop,
  buildBusy = false,
  allIngredientsLoading = false,
  allIngredientsError = false,
  onRetryAllIngredients = null,
}) {
  const groceryMeals = plannerGroceryMeals(assigned);
  const missing = plannerTotalMissingCount(groceryMeals);
  const isHeavy = plannerState === 'empty-pantry';
  const nothingMissing = missing === 0;
  const title = isHeavy ? 'Big shop ready' : 'Missing items';
  const subtitle = `${missing} missing · ${groceryMeals.length} meal${groceryMeals.length !== 1 ? 's' : ''}`;

  return (
    <section className={`mobile-grocery-section${expanded ? ' mobile-grocery-section--open' : ''}`}>
      <button
        type="button"
        className="mobile-grocery-section-header"
        aria-expanded={expanded}
        aria-controls="mobile-grocery-panel"
        disabled={!!buildBusy}
        onClick={() => onToggleExpanded?.()}
      >
        <div className="mobile-grocery-section-heading">
          <div className="mobile-grocery-section-title">{title}</div>
          <div className="mobile-grocery-section-sub">{subtitle}</div>
        </div>
        <span className="mobile-grocery-section-chevron" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      <div id="mobile-grocery-panel" className="mobile-grocery-cta">
        {expanded ? (
          <CompactGroceryRail
            assigned={assigned}
            filter={groceryFilter}
            onFilter={onGroceryFilter}
            onBuildGroceryList={onAddMissingToShop}
            buildBusy={buildBusy}
            showHeader={false}
            showUtilityActions={false}
            allIngredientsLoading={allIngredientsLoading}
            allIngredientsError={allIngredientsError}
            onRetryAllIngredients={onRetryAllIngredients}
          />
        ) : (
          <button
            type="button"
            className={`planner-btn ${isHeavy ? 'btn-sharp' : 'btn-primary'} planner-grocery-sheet-btn`}
            disabled={!!buildBusy || nothingMissing}
            onClick={() => {
              if (nothingMissing) return;
              onAddMissingToShop?.();
            }}
          >
            {buildBusy ? 'Adding…' : nothingMissing ? 'All set' : 'Add missing items to Shop →'}
          </button>
        )}
      </div>
    </section>
  );
}
